import { useEffect, useState } from "react";
import { logger } from "@/lib/logger";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ClipboardList } from "lucide-react";
import { stageViews } from "@/lib/stages";

const fmt = (n) => Math.round(Number(n || 0)).toLocaleString("en-IN"); // production units are whole pieces

function Stat({ label, value, cls, hint }) {
  return (
    <div className="text-center">
      <p className="text-[10px] uppercase tracking-wide text-slate-400">{label}</p>
      <p className={`font-mono-data text-lg font-semibold ${cls || "text-slate-700"}`}>{value}</p>
      {hint && <p className="text-[10px] text-slate-400">{hint}</p>}
    </div>
  );
}

// ctx = { order, idx }
export default function StageLogDialog({ ctx, onClose, onSubmit }) {
  const [qty, setQty] = useState("");
  const [saving, setSaving] = useState(false);
  const resetKey = ctx ? `${ctx.order.id}:${ctx.idx}` : "";
  useEffect(() => { if (resetKey) setQty(""); }, [resetKey]);
  if (!ctx) return null;
  const { order, idx } = ctx;
  const views = stageViews(order);
  const v = views[idx];
  if (!v) return null;
  const target = Number(order.target_quantity) || 0;
  const num = Number(qty);
  const valid = qty !== "" && num > 0 && num <= v.available + 1e-6;
  const prevName = idx > 0 ? views[idx - 1].name : null;
  const willCompleteOrder = v.isFinal && valid && v.done + num >= target - 1e-6;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setSaving(true);
    try { await onSubmit(order, idx, num); onClose(); }
    catch (err) { logger.error("Stage quantity log failed (toast shown by caller):", err); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="stage-log-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><ClipboardList className="h-4 w-4 text-blue-600" /> Log Quantity · {order.order_code}</DialogTitle>
          <DialogDescription>Stage {idx + 1} · <b>{v.name}</b> — {order.product_name}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="rounded-lg border border-slate-200 p-3 grid grid-cols-3 gap-2">
            <Stat label="Available to Start" value={fmt(v.available)} cls="text-blue-700" hint={prevName ? `from ${prevName}` : "order quantity"} />
            <Stat label="Completed Here" value={fmt(v.done)} hint={`of ${fmt(target)}`} />
            <Stat label="Remaining" value={fmt(Math.max(0, target - v.done))} hint="to finish stage" />
          </div>
          <div className="space-y-1.5">
            <Label>Quantity Completed at This Stage</Label>
            <div className="flex gap-2">
              <Input type="number" min="0.001" step="any" max={v.available} value={qty} onChange={(e) => setQty(e.target.value)} placeholder={`max ${fmt(v.available)}`} required autoFocus className="font-mono-data" data-testid="stage-log-qty" />
              <Button type="button" variant="outline" onClick={() => setQty(String(v.available))} className="shrink-0 text-xs" data-testid="stage-log-max">Max ({fmt(v.available)})</Button>
            </div>
            {qty !== "" && num > v.available && <p className="text-[11px] text-red-600" data-testid="stage-log-over">Only {fmt(v.available)} available — the previous stage must hand off more first.</p>}
            {v.isFinal && valid && (
              <p className="text-[11px] text-emerald-600" data-testid="stage-log-final-hint">
                ✓ Final stage — {fmt(num)} pcs will be added to Finished Goods stock.{willCompleteOrder ? " The order will be marked Completed." : ""}
              </p>
            )}
          </div>
          <DialogFooter className="justify-between">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={!valid || saving} className="bg-blue-600 hover:bg-blue-700" data-testid="stage-log-submit">
              {saving ? "Logging…" : valid ? `Log ${fmt(num)} pcs` : "Log Quantity"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
